import type { MessageInvalidation } from '../shell/chat/chat-contracts.js';

/**
 * src/journal/vault-invalidation-bus.ts
 * 
 * Ephemeral same-origin invalidation bus for VaultDB state updates.
 * Transmits ONLY lightweight MessageInvalidation signals, never full message bodies or binary payloads.
 */

const INVALIDATION_CHANNEL_NAME = 'sovereign-chat-invalidation';

export class VaultInvalidationBus {
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<(event: MessageInvalidation) => void>();

  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(INVALIDATION_CHANNEL_NAME);
        this.channel.onmessage = (e) => {
          if (e.data && e.data.type && e.data.meshId) {
            this.notify(e.data as MessageInvalidation);
          }
        };
      } catch (err) {
        console.warn('BroadcastChannel not supported in this context, falling back to local bus', err);
      }
    }
  }

  publish(invalidation: MessageInvalidation): void {
    // 1. Broadcast to other tabs/windows
    if (this.channel) {
      try {
        this.channel.postMessage(invalidation);
      } catch (err) {
        console.warn('Failed to postMessage to invalidation channel', err);
      }
    }

    // 2. Notify local observers in current context
    this.notify(invalidation);
  }

  subscribe(callback: (event: MessageInvalidation) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notify(event: MessageInvalidation): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (err) {
        console.error('Error in invalidation listener', err);
      }
    }
  }

  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

export const globalInvalidationBus = new VaultInvalidationBus();
